import { ref, nextTick } from "vue";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  documentId,
} from "firebase/firestore";
import { useNuxtApp } from "#app";
import {
  isSellableVariant,
  readVariantQty,
} from "~/composables/product/variantDisplay";
import type { CartItem } from "~/stores/cart";

/* ====================================================================
 *  useCartAvailability — наявність позицій кошика: чи можна продати і
 *  скільки максимум. Best-effort і НЕ блокуюча: немає сигналів —
 *  вважаємо доступним (авторитет усе одно на боці placeOrder).
 *
 *  Резолв триступеневий: варіант за variantId → варіант за кольором/
 *  розміром (legacy-позиції без variantId) → сам товар.
 *
 *  Тут же живе скелетон: він ховається лише після ПЕРШОГО резолву,
 *  щоб користувач не бачив, як недоступні позиції стрибають донизу.
 * ==================================================================== */

export interface AvailabilityRecord {
  /** false = знято з продажу або немає; null = сигналів немає */
  inStock: boolean | null;
  /** відомий залишок; null = невідомо, не обмежуємо */
  maxQty: number | null;
}

type AnyRecord = Record<string, unknown>;

const SKELETON_MIN_MS = 300;

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Залишок варіанта. Крім плоских полів (readVariantQty) дивиться
 * вкладені форми — вони зустрічались лише в кошикових даних, тому
 * навмисно НЕ переїхали в спільний variantDisplay.
 */
function readStockQty(obj: unknown): number | null {
  const flat = readVariantQty(obj);
  if (flat !== null) return Math.max(0, Math.floor(flat));

  if (!obj || typeof obj !== "object") return null;
  const o = obj as AnyRecord;
  const nested = [(o.stock as AnyRecord)?.qty, (o.inventory as AnyRecord)?.qty];
  for (const v of nested) {
    if (typeof v === "number") return Math.max(0, Math.floor(v));
  }
  return null;
}

function normStr(v: unknown): string {
  return String(v || "")
    .trim()
    .toLowerCase();
}

function readField(obj: unknown, keys: string[]): string {
  if (!obj || typeof obj !== "object") return "";
  const o = obj as AnyRecord;
  for (const k of keys) {
    const val = o[k];
    if (typeof val === "string" && val.trim()) return val;
    if (val && typeof val === "object") {
      const vo = val as AnyRecord;
      const vv = vo.value || vo.label || vo.name;
      if (typeof vv === "string" && vv.trim()) return vv;
    }
  }
  return "";
}

/** Legacy-позиції без variantId: шукаємо варіант за кольором/розміром. */
function findVariantByCartProps(
  variants: AnyRecord[],
  item: Partial<CartItem>,
): AnyRecord | null {
  if (!variants.length) return null;

  const cColor = normStr(item?.color);
  const cSize = normStr(item?.size);
  if (!cColor && !cSize) return null;

  return (
    variants.find((v) => {
      const vColor = normStr(readField(v, ["color", "colour", "colorValue"]));
      const vSize = normStr(readField(v, ["size", "sizeValue"]));

      if (cColor && cSize) return vColor === cColor && vSize === cSize;
      if (cColor) return vColor === cColor;
      if (cSize) return vSize === cSize;
      return false;
    }) || null
  );
}

function parseVariantIdFromKey(key: unknown): string {
  const parts = String(key || "").split("|");
  return parts.length >= 2 ? parts[1] : "";
}

function chunk<T>(arr: T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

export function useCartAvailability() {
  const { $db } = useNuxtApp();

  const availabilityByKey = ref<Record<string, AvailabilityRecord>>({});

  const showSkeleton = ref(true);
  const hasResolvedOnce = ref(false);
  const skeletonStartedAt = ref(Date.now());
  let resolveSeq = 0;

  async function hideSkeletonOnce(mySeq: number) {
    if (hasResolvedOnce.value) return;

    // даємо Vue застосувати availability -> порядок позицій -> рендер
    await nextTick();

    // мінімальний час показу: інакше скелетон може зникнути до першого кадру
    const elapsed = Date.now() - Number(skeletonStartedAt.value || 0);
    const waitMs = Math.max(0, SKELETON_MIN_MS - elapsed);
    if (waitMs) await sleep(waitMs);

    if (mySeq === resolveSeq) {
      showSkeleton.value = false;
      hasResolvedOnce.value = true;
    }
  }

  /** Сховати скелетон примусово (розмонтування сторінки). */
  function stopSkeleton() {
    showSkeleton.value = false;
  }

  function isItemInStock(it: Partial<CartItem> | undefined): boolean {
    return availabilityByKey.value?.[String(it?.key || "")]?.inStock !== false;
  }

  function getMaxQty(it: Partial<CartItem> | undefined): number | null {
    const n = availabilityByKey.value?.[String(it?.key || "")]?.maxQty;
    return typeof n === "number" && Number.isFinite(n) ? n : null;
  }

  async function refreshAvailability(items: Partial<CartItem>[]) {
    const mySeq = ++resolveSeq;

    try {
      const list = Array.isArray(items) ? items : [];
      const productIds = Array.from(
        new Set(list.map((x) => String(x?.productId || "")).filter(Boolean)),
      );

      if (!productIds.length) {
        if (mySeq !== resolveSeq) return;
        availabilityByKey.value = {};
        await hideSkeletonOnce(mySeq);
        return;
      }

      // 1) документи товарів (фолбек наявності на рівні товару)
      const productMap = new Map<string, AnyRecord>();
      for (const ids of chunk(productIds, 10)) {
        const snap = await getDocs(
          query(collection($db, "products"), where(documentId(), "in", ids)),
        );
        snap.forEach((d) => productMap.set(d.id, d.data() as AnyRecord));
      }

      // 2) варіанти — з кешем на продукт і на окремий документ
      const variantsListCache = new Map<string, AnyRecord[]>();
      const variantDocCache = new Map<string, AnyRecord | null>();

      async function getVariantsList(pid: string) {
        const cached = variantsListCache.get(pid);
        if (cached) return cached;
        try {
          const snap = await getDocs(
            collection($db, "products", pid, "variants"),
          );
          const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          variantsListCache.set(pid, arr);
          return arr;
        } catch {
          variantsListCache.set(pid, []);
          return [];
        }
      }

      async function getVariantDoc(pid: string, vid: string) {
        const k = `${pid}|${vid}`;
        if (variantDocCache.has(k)) return variantDocCache.get(k) ?? null;
        try {
          const snap = await getDoc(doc($db, "products", pid, "variants", vid));
          const data = snap.exists() ? (snap.data() as AnyRecord) : null;
          variantDocCache.set(k, data);
          return data;
        } catch {
          variantDocCache.set(k, null);
          return null;
        }
      }

      const next: Record<string, AvailabilityRecord> = {};

      for (const it of list) {
        const pid = String(it?.productId || "");
        const key = String(it?.key || "");
        if (!pid || !key) continue;

        // 1) за variantId — основний шлях
        const variantId =
          String(it?.variantId || "") || parseVariantIdFromKey(it?.key);

        if (variantId) {
          const vDoc = await getVariantDoc(pid, variantId);
          const vAvail = isSellableVariant(vDoc);
          const vQty = readStockQty(vDoc);

          if (vAvail !== null || vQty !== null) {
            next[key] = {
              inStock: vAvail,
              maxQty: vAvail === false ? 0 : vQty,
            };
            continue;
          }
        }

        // 2) legacy: варіант за кольором/розміром
        if (it?.color || it?.size) {
          const vByProps = findVariantByCartProps(
            await getVariantsList(pid),
            it,
          );
          const vAvail2 = isSellableVariant(vByProps);
          const vQty2 = readStockQty(vByProps);

          if (vAvail2 !== null || vQty2 !== null) {
            next[key] = {
              inStock: vAvail2,
              maxQty: vAvail2 === false ? 0 : vQty2,
            };
            continue;
          }
        }

        // 3) рівень товару: лише наявність, залишок невідомий
        const pAvail = isSellableVariant(productMap.get(pid));
        if (pAvail !== null) {
          next[key] = { inStock: pAvail, maxQty: pAvail === false ? 0 : null };
        }
      }

      if (mySeq !== resolveSeq) return;

      availabilityByKey.value = next;
      await hideSkeletonOnce(mySeq);
    } catch {
      // non-blocking: навіть на помилці прибираємо скелетон, щоб кошик був робочим
      if (!hasResolvedOnce.value && mySeq === resolveSeq) {
        await hideSkeletonOnce(mySeq);
      }
    }
  }

  return {
    availabilityByKey,
    showSkeleton,
    refreshAvailability,
    isItemInStock,
    getMaxQty,
    stopSkeleton,
  };
}
